import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Loader2, Upload, FileArchive, CheckCircle, AlertTriangle, X, ArrowRight, Sparkles, Eye, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import JSZip from 'jszip';
import { TYPE_OPTIONS, FAMILY_COLORS, isRowValid } from './zipUploadHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ZipUploadModal({ open, onClose, membre, token, onCommitted }) {
  const [zipFile, setZipFile] = useState(null);
  const [step, setStep] = useState('select');
  const [previewData, setPreviewData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [selected, setSelected] = useState({}); // filename → true
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [docPreview, setDocPreview] = useState(null); // { name, url, mime }
  const [extractingName, setExtractingName] = useState(null);
  const [zipObject, setZipObject] = useState(null); // JSZip instance for client-side extraction
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setZipFile(null);
    setStep('select');
    setPreviewData(null);
    setOverrides({});
    setSelected({});
    setCommitResult(null);
    if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
    setDocPreview(null);
    setZipObject(null);
  }, [docPreview]);

  const handleClose = () => { reset(); onClose?.(); };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) { toast.error('Sélectionnez un fichier .zip'); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error('ZIP trop volumineux (max 200 Mo)'); return; }
    setZipFile(f);
  };

  const runPreview = async () => {
    if (!zipFile || !membre?.id) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', zipFile);
      // Lance en parallèle : extraction client-side + analyse serveur
      const [serverRes, jsz] = await Promise.all([
        axios.post(
          `${API}/api/admin/membres/${membre.id}/documents/upload-zip-preview`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        ),
        JSZip.loadAsync(zipFile),
      ]);
      setPreviewData(serverRes.data);
      setZipObject(jsz);
      // Présélectionne TOUT sauf les doublons (already_in_ged) → opt-in explicite admin
      const sel = {};
      (serverRes.data.items || []).forEach((it) => {
        sel[it.filename] = !it.already_in_ged;
      });
      setSelected(sel);
      setStep('preview');
      if (serverRes.data.duplicates_count > 0) {
        toast.warning(
          `${serverRes.data.duplicates_count} document(s) déjà présent(s) dans la GED — décochés par défaut`,
          { duration: 6000 }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'analyse du ZIP");
    } finally {
      setPreviewing(false);
    }
  };

  const updateOverride = (filename, field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [filename]: { ...prev[filename], [field]: value },
    }));
  };

  const toggleSelect = (filename) => {
    setSelected((p) => ({ ...p, [filename]: !p[filename] }));
  };
  const toggleSelectAll = () => {
    if (!previewData) return;
    const allSelected = previewData.items.every((it) => selected[it.filename]);
    const next = {};
    previewData.items.forEach((it) => { next[it.filename] = !allSelected; });
    setSelected(next);
  };
  const selectOnlyValid = () => {
    if (!previewData) return;
    const next = {};
    previewData.items.forEach((it) => { next[it.filename] = isRowValid(it, overrides); });
    setSelected(next);
  };

  const handlePreviewDoc = async (filename) => {
    if (!zipObject) { toast.error("Archive non chargée pour l'aperçu"); return; }
    setExtractingName(filename);
    try {
      // Cherche le file dans l'archive (peut avoir un chemin parent)
      let entry = zipObject.file(filename);
      if (!entry) {
        zipObject.forEach((path, e) => {
          if (!entry && path.split('/').pop() === filename) entry = e;
        });
      }
      if (!entry) { toast.error('Fichier introuvable dans le ZIP'); return; }
      const ext = filename.toLowerCase().split('.').pop();

      // V36 — Rendu .docx via mammoth (HTML inline)
      if (ext === 'docx') {
        try {
          const arrayBuffer = await entry.async('arraybuffer');
          const mammoth = (await import('mammoth')).default || (await import('mammoth'));
          const result = await mammoth.convertToHtml({ arrayBuffer });
          const safeHtml = result.value || '<p class="text-slate-400 italic">Document vide ou non lisible.</p>';
          if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
          setDocPreview({ name: filename, html: safeHtml, mime: 'text/html-docx' });
          return;
        } catch (docxErr) {
          // Fallback : download link
          const blob = await entry.async('blob');
          const dlBlob = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = URL.createObjectURL(dlBlob);
          if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
          setDocPreview({ name: filename, url, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', renderError: String(docxErr).slice(0, 200) });
          return;
        }
      }

      // Autres formats : blob direct
      const mime = ext === 'pdf' ? 'application/pdf'
        : ['jpg', 'jpeg'].includes(ext) ? 'image/jpeg'
        : ext === 'png' ? 'image/png'
        : ext === 'webp' ? 'image/webp'
        : 'application/octet-stream';
      const blob = await entry.async('blob');
      const typedBlob = new Blob([blob], { type: mime });
      const url = URL.createObjectURL(typedBlob);
      if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
      setDocPreview({ name: filename, url, mime });
    } catch {
      toast.error("Erreur d'extraction du fichier");
    } finally {
      setExtractingName(null);
    }
  };

  const closeDocPreview = () => {
    if (docPreview?.url) URL.revokeObjectURL(docPreview.url);
    setDocPreview(null);
  };

  const runCommit = async (filenamesOverride = null) => {
    if (!zipFile || !membre?.id) return;
    const filesToImport = filenamesOverride
      || previewData.items.filter((it) => selected[it.filename]).map((it) => it.filename);
    if (filesToImport.length === 0) {
      toast.error('Aucun document sélectionné');
      return;
    }
    setCommitting(true);
    setStep('committing');
    try {
      const fd = new FormData();
      fd.append('file', zipFile);
      const payload = { ...overrides, _selected_filenames: filesToImport };
      fd.append('overrides_json', JSON.stringify(payload));
      const res = await axios.post(
        `${API}/api/admin/membres/${membre.id}/documents/upload-zip-commit`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setCommitResult(res.data);
      setStep('done');
      toast.success(`${res.data.imported_count} document(s) importé(s)`);
      onCommitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'import");
      setStep('preview');
    } finally {
      setCommitting(false);
    }
  };

  const commitSingle = (filename) => runCommit([filename]);

  const stats = useMemo(() => {
    if (!previewData) return { total: 0, selected: 0, valid: 0 };
    const sel = previewData.items.filter((it) => selected[it.filename]).length;
    const val = previewData.items.filter((it) => isRowValid(it, overrides)).length;
    return { total: previewData.items.length, selected: sel, valid: val };
  }, [previewData, selected, overrides]);

  const allSelected = previewData && previewData.items.every((it) => selected[it.filename]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="zip-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileArchive className="h-5 w-5 text-indigo-600" />
            Import ZIP — Documents historiques
            <Badge variant="outline" className="ml-2 text-[10px]">
              {membre?.raison_sociale || `${membre?.prenom || ''} ${membre?.nom || ''}`}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Étape 1 — Sélection du ZIP */}
        {step === 'select' && (
          <div className="space-y-4 pt-2">
            <Alert className="bg-indigo-50 border-indigo-200">
              <Sparkles className="h-4 w-4 text-indigo-500" />
              <AlertDescription className="text-indigo-700 text-xs">
                Glissez un ZIP. Vous pourrez prévisualiser chaque PDF/image, corriger type & date, puis valider
                en masse ou unitairement.
              </AlertDescription>
            </Alert>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                zipFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40'
              }`}
              onClick={() => fileRef.current?.click()}
              data-testid="zip-dropzone"
            >
              <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
              {zipFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileArchive className="h-8 w-8 text-emerald-600" />
                  <div>
                    <div className="text-sm font-semibold text-emerald-700">{zipFile.name}</div>
                    <div className="text-xs text-emerald-500">{(zipFile.size / 1024 / 1024).toFixed(2)} Mo</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setZipFile(null); }} className="p-1 hover:bg-red-100 rounded">
                    <X className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">Glissez un fichier .zip ici ou cliquez</p>
                  <p className="text-xs text-slate-400 mt-1">Max 200 Mo</p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="zip-cancel-btn">Annuler</Button>
              <Button
                onClick={runPreview}
                disabled={!zipFile || previewing}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="zip-preview-btn"
              >
                {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                Analyser le ZIP
              </Button>
            </div>
          </div>
        )}

        {/* Étape 2 — Aperçu et corrections */}
        {step === 'preview' && previewData && (
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-3 border">
                <div className="text-2xl font-bold text-slate-800">{previewData.total_files}</div>
                <div className="text-[10px] uppercase text-slate-500 mt-1">Fichiers</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                <div className="text-2xl font-bold text-emerald-700">{previewData.high_confidence}</div>
                <div className="text-[10px] uppercase text-emerald-600 mt-1">Confiance haute</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="text-2xl font-bold text-amber-700">{previewData.low_confidence}</div>
                <div className="text-[10px] uppercase text-amber-600 mt-1">À vérifier</div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                <div className="text-2xl font-bold text-indigo-700">{stats.valid}</div>
                <div className="text-[10px] uppercase text-indigo-600 mt-1">Datés & typés</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{stats.selected}</div>
                <div className="text-[10px] uppercase text-blue-600 mt-1">Sélectionnés</div>
              </div>
            </div>

            {/* Toolbar sélection */}
            <div className="flex items-center justify-between gap-2 bg-slate-50 border rounded-lg px-3 py-2 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSelectAll}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-white border hover:bg-slate-100"
                  data-testid="zip-select-all-btn"
                >
                  <span className={`h-3.5 w-3.5 rounded border flex items-center justify-center ${allSelected ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300'}`}>
                    {allSelected && <Check className="h-2.5 w-2.5 text-white" />}
                  </span>
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
                <button
                  onClick={selectOnlyValid}
                  className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  data-testid="zip-select-valid-btn"
                >
                  Sélectionner uniquement les complets ({stats.valid})
                </button>
              </div>
              <div className="text-xs text-slate-500">
                <strong className="text-slate-800">{stats.selected}</strong> sur {stats.total} sélectionnés
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-2 text-center w-8"></th>
                    <th className="px-2 py-2 text-left font-semibold">Fichier</th>
                    <th className="px-2 py-2 text-left font-semibold">Famille</th>
                    <th className="px-2 py-2 text-left font-semibold">Type</th>
                    <th className="px-2 py-2 text-left font-semibold">Date d&apos;origine</th>
                    <th className="px-2 py-2 text-center font-semibold">Aperçu</th>
                    <th className="px-2 py-2 text-center font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.items.map((it) => {
                    const ov = overrides[it.filename] || {};
                    const currentType = ov.type_document || it.type_document;
                    const currentDate = ov.date_origine !== undefined ? ov.date_origine : (it.date_origine_auto || '');
                    const isSel = !!selected[it.filename];
                    const valid = isRowValid(it, overrides);
                    return (
                      <tr key={it.filename} className={`border-t hover:bg-slate-50 ${isSel ? 'bg-indigo-50/30' : ''}`} data-testid={`zip-row-${it.filename}`}>
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => toggleSelect(it.filename)}
                            className={`h-4 w-4 rounded border flex items-center justify-center ${isSel ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300 hover:border-indigo-300'}`}
                            data-testid={`zip-check-${it.filename}`}
                          >
                            {isSel && <Check className="h-3 w-3 text-white" />}
                          </button>
                        </td>
                        <td className="px-2 py-2 max-w-[220px]">
                          <div className="truncate text-xs font-medium text-slate-800" title={it.filename}>
                            {it.filename}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            {it.confidence === 'high' ? (
                              <span className="inline-flex items-center gap-0.5 text-[9px] text-emerald-600">
                                <CheckCircle className="h-2.5 w-2.5" /> auto-détecté
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-600">
                                <AlertTriangle className="h-2.5 w-2.5" /> à vérifier
                              </span>
                            )}
                            {it.is_antecedent && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded">Antériorité</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] border ${FAMILY_COLORS[it.family] || FAMILY_COLORS.Autre}`}>
                            {it.family}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={currentType} onValueChange={(v) => updateOverride(it.filename, 'type_document', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TYPE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <DatePickerFr
                            value={currentDate || ''}
                            onChange={(v) => updateOverride(it.filename, 'date_origine', v || null)}
                            className="h-7 text-xs w-36"
                            id={`zip-date-${it.filename}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            onClick={() => handlePreviewDoc(it.filename)}
                            disabled={extractingName === it.filename}
                            data-testid={`zip-eye-${it.filename}`}
                            title="Aperçu du document"
                          >
                            {extractingName === it.filename ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                          </Button>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[10px] font-semibold border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => commitSingle(it.filename)}
                            disabled={committing || !valid}
                            data-testid={`zip-validate-single-${it.filename}`}
                            title={valid ? "Importer uniquement ce document" : "Renseignez d'abord type & date"}
                          >
                            <Check className="h-3 w-3 mr-0.5" />
                            Valider
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-2 border-t bg-white sticky bottom-0 -mx-6 px-6 pb-2">
              <Button variant="outline" onClick={() => setStep('select')} data-testid="zip-back-btn">
                ← Retour
              </Button>
              <Button
                onClick={() => runCommit()}
                disabled={committing || stats.selected === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="zip-commit-btn"
              >
                {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Importer {stats.selected} document{stats.selected > 1 ? 's' : ''} sélectionné{stats.selected > 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Étape 3 — En cours */}
        {step === 'committing' && (
          <div className="py-12 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">Import en cours…</p>
            <p className="text-xs text-slate-400 mt-1">Synchronisation avec la GED</p>
          </div>
        )}

        {/* Étape 4 — Récap */}
        {step === 'done' && commitResult && (
          <div className="space-y-4 pt-2">
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <AlertDescription className="text-emerald-700 text-sm">
                <strong>{commitResult.imported_count}</strong> document(s) importé(s) avec succès dans la GED.
                {commitResult.skipped_count > 0 && (
                  <span className="text-slate-600"> {commitResult.skipped_count} ignoré(s).</span>
                )}
                {commitResult.errors_count > 0 && (
                  <span className="text-amber-700"> {commitResult.errors_count} erreur(s).</span>
                )}
              </AlertDescription>
            </Alert>

            {commitResult.errors?.length > 0 && (
              <div className="border border-amber-200 rounded-lg overflow-hidden">
                <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Fichiers en erreur :</div>
                <ul className="text-xs divide-y divide-amber-100">
                  {commitResult.errors.map((e, idx) => (
                    <li key={idx} className="px-3 py-2 flex justify-between">
                      <span className="truncate">{e.filename}</span>
                      <span className="text-amber-600">{e.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  // Retourner à l'étape preview pour traiter les restants
                  const imported = new Set((commitResult.saved || []).map((s) => s.filename));
                  const remaining = {};
                  previewData?.items.forEach((it) => {
                    if (!imported.has(it.filename)) remaining[it.filename] = true;
                  });
                  setSelected(remaining);
                  setCommitResult(null);
                  setStep('preview');
                }}
                disabled={!previewData || !commitResult.saved?.length}
                data-testid="zip-continue-btn"
              >
                ← Continuer avec les restants
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} data-testid="zip-new-btn">Nouveau ZIP</Button>
                <Button onClick={handleClose} className="bg-indigo-600 hover:bg-indigo-700" data-testid="zip-close-btn">
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Lightbox aperçu document (extraction client-side) */}
        {docPreview && (
          <div
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
            onClick={closeDocPreview}
            data-testid="doc-preview-lightbox"
          >
            <div
              className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <div className="flex items-center gap-2 min-w-0">
                  <Eye className="h-4 w-4 text-indigo-500" />
                  <span className="text-sm font-semibold truncate">{docPreview.name}</span>
                </div>
                <button onClick={closeDocPreview} className="p-1 hover:bg-slate-100 rounded" data-testid="doc-preview-close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden bg-slate-100">
                {docPreview.mime === 'application/pdf' ? (
                  <iframe src={docPreview.url} title={docPreview.name} className="w-full h-[80vh]" />
                ) : docPreview.mime === 'text/html-docx' ? (
                  <div className="h-[80vh] overflow-y-auto bg-white">
                    <div className="max-w-3xl mx-auto p-8">
                      <div className="mb-3 pb-3 border-b text-[10px] uppercase tracking-wide text-slate-400 flex items-center justify-between">
                        <span>📄 Aperçu Word (.docx) — rendu HTML</span>
                        <span className="text-amber-600">Mise en page simplifiée</span>
                      </div>
                      {/* eslint-disable-next-line react/no-danger */}
                      <div
                        className="prose prose-sm max-w-none text-slate-800 leading-relaxed [&_p]:my-2 [&_h1]:text-lg [&_h2]:text-base [&_table]:border [&_td]:border [&_td]:px-2 [&_th]:border [&_th]:px-2 [&_th]:bg-slate-50"
                        dangerouslySetInnerHTML={{ __html: docPreview.html }}
                        data-testid="docx-html-content"
                      />
                    </div>
                  </div>
                ) : docPreview.mime.startsWith('image/') ? (
                  <img src={docPreview.url} alt={docPreview.name} className="max-w-full max-h-[80vh] mx-auto object-contain" />
                ) : (
                  <div className="p-8 text-center text-sm text-slate-500">
                    Aperçu non disponible pour ce format.{' '}
                    <a href={docPreview.url} download={docPreview.name} className="text-indigo-600 underline">
                      Télécharger
                    </a>
                    {docPreview.renderError && (
                      <p className="mt-2 text-[10px] text-amber-600 font-mono">{docPreview.renderError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
